import type { FC } from 'react';
import React, { useContext, useCallback } from 'react';

import Button from '@atlaskit/button/custom-theme-button/custom-theme-button';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics/use-ai-events-instrumentation';

import { DISCOVER_ITEM_ON_HOME_PAGE_CLICK } from '@confluence/browser-metrics/entry-points/viewPageTransitionSourceConstants.ts';
import { setViewTransitionSource } from '@confluence/browser-metrics/entry-points/viewTransitionSource.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { generatePathWithFeatureCodesQueryParam } from '@confluence/feature-codes/entry-points/featureCodes.ts';
import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel.ts';

import type { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../../Feed.tsx';

import { FeedCardContext } from './FeedCardContext.ts';

const fullSizeButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			position: 'absolute',
			top: '0px',
			left: '0px',
			width: '100%',
			height: '100%',
		},
		spinnerStyles,
	};
};

// It is invalid (and not accessible) to put an html button/link inside another button.
// so to make the full card clickable without nesting buttons, we style it absolute and full size.
type Props = {
	href: string;
	contentType?: string;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortBy?: FollowingFeedSortByValue;
	hasAiSnippet?: boolean;
};
export const FeedCardInvisibleFullSizedLink: FC<Props> = ({
	href,
	contentType,
	popularFeedTimeGranularity,
	followingFeedSortBy,
	hasAiSnippet,
}) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);
	const { push } = useRouteActions();

	const { trackAIResultAction } = useAIEventsInstrumentation();

	const feedType = popularFeedTimeGranularity ? 'popularFeed' : 'followingFeed';

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
			if (hasAiSnippet) {
				trackAIResultAction(`${feedType}aiSnippetClicked`);
			}

			if (contentType && contentType === 'page') {
				setViewTransitionSource(DISCOVER_ITEM_ON_HOME_PAGE_CLICK);
			}
			fireAnalyticsEvent({
				type: 'sendUIEvent',
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'feedCard',
				includeNavdex: true,
				additionalAttributes: {
					popularFeedTimeGranularity,
					followingFeedSortBy,
				},
			});

			e.preventDefault();
			if (e.metaKey || e.ctrlKey) {
				window.open(generatePathWithFeatureCodesQueryParam(href, [FeatureCode.HOME_FEED]));
			} else {
				push(generatePathWithFeatureCodesQueryParam(href, [FeatureCode.HOME_FEED]));
			}
		},
		[
			contentType,
			fireAnalyticsEvent,
			popularFeedTimeGranularity,
			followingFeedSortBy,
			href,
			push,
			hasAiSnippet,
			trackAIResultAction,
			feedType,
		],
	);

	return (
		<Button
			href={href}
			appearance="link"
			theme={fullSizeButtonTheme}
			onClick={handleClick}
			testId="feed-card-body-link"
			aria-hidden
			tabIndex={-1}
		/>
	);
};
